import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, Users as UsersIcon, Clock as ClockIcon, CheckCircle2, XCircle, Baby, Trash2, Heart, UserCircle2 } from "lucide-react";
import { WhatsAppFamilyButton } from "./WhatsAppShareButton";

/**
 * Family Accounts (patient view) — a verified parent can add dependents
 * (child, elderly parent, spouse). Each submission waits for admin approval.
 * Once approved, the dependent shows up in the Patient Dashboard's profile
 * switcher so scans + consults happen under that dependent.
 */
export function FamilyTab({ dependents, reload, parentUser, parentVitals }) {
    const [open, setOpen] = useState(false);

    const groups = {
        approved: dependents.filter((d) => d.status === "approved"),
        pending: dependents.filter((d) => d.status === "pending"),
        rejected: dependents.filter((d) => d.status === "rejected"),
    };

    const onRemove = async (dep) => {
        const yes = window.confirm(`Remove ${dep.name}? Their scans and vitals will be permanently erased from your account.`);
        if (!yes) return;
        try {
            await api.delete(`/me/dependents/${dep.id}`);
            toast.success("Dependent removed.");
            reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Remove failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="tab-family">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl font-semibold">Family accounts</h2>
                    <p className="text-sm text-gray-500 mt-1 max-w-xl">
                        Manage dependents (children, elderly parents, spouses) under your single Medx AI login.
                        Each new dependent is reviewed by an admin before you can switch to their profile.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <WhatsAppFamilyButton
                        parentUser={parentUser}
                        parentVitals={parentVitals}
                        dependents={dependents}
                        label="WhatsApp family report"
                    />
                    <button
                        onClick={() => setOpen(true)}
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid="family-add-dependent-btn"
                    >
                        <UserPlus className="w-4 h-4" /> Add dependent
                    </button>
                </div>
            </div>

            {dependents.length === 0 ? (
                <div className="card-soft p-10 text-center" data-testid="family-empty">
                    <div className="w-16 h-16 mx-auto rounded-full bg-orange-50 grid place-items-center mb-4">
                        <UsersIcon className="w-7 h-7 text-[var(--medx-orange)]" />
                    </div>
                    <div className="font-display text-2xl font-semibold mb-2">No dependents yet</div>
                    <p className="text-gray-600 max-w-md mx-auto mb-6">
                        One household · one login. Add a child, parent or spouse and manage their health records right here.
                    </p>
                    <button onClick={() => setOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="family-empty-add">
                        <UserPlus className="w-4 h-4" /> Add your first dependent
                    </button>
                </div>
            ) : (
                <div className="space-y-6">
                    {groups.approved.length > 0 && (
                        <Section title="Approved" tone="emerald">
                            {groups.approved.map((d) => <DependentCard key={d.id} dep={d} onRemove={onRemove} />)}
                        </Section>
                    )}
                    {groups.pending.length > 0 && (
                        <Section title="Awaiting admin approval" tone="amber">
                            {groups.pending.map((d) => <DependentCard key={d.id} dep={d} onRemove={onRemove} />)}
                        </Section>
                    )}
                    {groups.rejected.length > 0 && (
                        <Section title="Rejected" tone="gray">
                            {groups.rejected.map((d) => <DependentCard key={d.id} dep={d} onRemove={onRemove} />)}
                        </Section>
                    )}
                </div>
            )}

            {open && <AddDependentModal onClose={() => setOpen(false)} onCreated={() => { setOpen(false); reload(); }} />}
        </div>
    );
}

function Section({ title, tone, children }) {
    const toneMap = {
        emerald: "text-emerald-700",
        amber: "text-amber-700",
        gray: "text-gray-500",
    };
    return (
        <div>
            <div className={`text-[11px] font-mono tracking-[0.22em] mb-2 ${toneMap[tone] || "text-gray-500"}`}>{title.toUpperCase()}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
        </div>
    );
}

function DependentCard({ dep, onRemove }) {
    const relIcon = {
        child: Baby, spouse: Heart, parent: UserCircle2, sibling: UserCircle2, other: UserCircle2,
    }[dep.relationship] || UserCircle2;
    const RelIcon = relIcon;
    const statusPill = {
        approved: <span className="text-[10px] font-mono tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> APPROVED</span>,
        pending: <span className="text-[10px] font-mono tracking-wider px-2 py-1 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1"><ClockIcon className="w-3 h-3" /> PENDING</span>,
        rejected: <span className="text-[10px] font-mono tracking-wider px-2 py-1 rounded-full bg-gray-200 text-gray-600 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> REJECTED</span>,
    }[dep.status];
    return (
        <div className="card-soft p-5" data-testid={`dependent-card-${dep.id}`}>
            <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white">
                    <RelIcon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-semibold truncate">{dep.name}</div>
                    <div className="text-xs text-gray-500 capitalize">{dep.relationship} · age {dep.age}{dep.gender ? ` · ${dep.gender}` : ""}</div>
                </div>
                {statusPill}
            </div>
            {dep.status === "approved" && (
                <div className="text-xs text-gray-600 bg-emerald-50 rounded-lg p-3 mb-3">
                    <b>Health Score:</b> {dep.health_score || 0}/100 · switch to this profile from the top-right profile menu to upload scans or book consults.
                </div>
            )}
            {dep.status === "rejected" && dep.decision_notes && (
                <div className="text-xs text-red-700 bg-red-50 rounded-lg p-3 mb-3">
                    <b>Admin note:</b> {dep.decision_notes}
                </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs text-gray-500">
                <div>Added {new Date(dep.created_at).toLocaleDateString()}</div>
                <button
                    onClick={() => onRemove(dep)}
                    className="inline-flex items-center gap-1 text-gray-500 hover:text-red-600 font-medium"
                    data-testid={`dependent-remove-${dep.id}`}
                >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
            </div>
        </div>
    );
}

function AddDependentModal({ onClose, onCreated }) {
    const [first, setFirst] = useState("");
    const [last, setLast] = useState("");
    const [dob, setDob] = useState("");
    const [relationship, setRelationship] = useState("child");
    const [gender, setGender] = useState("");
    const [idNumber, setIdNumber] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/me/dependents", {
                first_name: first,
                last_name: last,
                date_of_birth: dob,
                relationship,
                gender: gender || undefined,
                id_number: idNumber || undefined,
            });
            toast.success("Dependent submitted for admin review.");
            onCreated?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not add dependent");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="add-dependent-modal">
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h3 className="font-display text-xl font-semibold">Add a dependent</h3>
                        <p className="text-xs text-gray-500 mt-1">Admin will verify this record before you can switch to their profile.</p>
                    </div>
                    <button type="button" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-gray-100 grid place-items-center text-gray-500" aria-label="Close">✕</button>
                </div>
                <div className="p-6 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] font-mono tracking-wider text-gray-500">FIRST NAME</label>
                            <input value={first} onChange={(e) => setFirst(e.target.value)} required placeholder="Jamie"
                                className="w-full mt-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                data-testid="dep-first-name" />
                        </div>
                        <div>
                            <label className="text-[11px] font-mono tracking-wider text-gray-500">LAST NAME</label>
                            <input value={last} onChange={(e) => setLast(e.target.value)} required placeholder="Doe"
                                className="w-full mt-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                data-testid="dep-last-name" />
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] font-mono tracking-wider text-gray-500">DATE OF BIRTH</label>
                        <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} required max={new Date().toISOString().slice(0, 10)}
                            className="w-full mt-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                            data-testid="dep-dob" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] font-mono tracking-wider text-gray-500">RELATIONSHIP</label>
                            <select value={relationship} onChange={(e) => setRelationship(e.target.value)}
                                className="w-full mt-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm bg-white"
                                data-testid="dep-relationship">
                                <option value="child">Child</option>
                                <option value="parent">Parent (elderly)</option>
                                <option value="spouse">Spouse</option>
                                <option value="sibling">Sibling</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[11px] font-mono tracking-wider text-gray-500">GENDER (OPTIONAL)</label>
                            <select value={gender} onChange={(e) => setGender(e.target.value)}
                                className="w-full mt-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm bg-white"
                                data-testid="dep-gender">
                                <option value="">—</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] font-mono tracking-wider text-gray-500">ID NUMBER (OPTIONAL)</label>
                        <input value={idNumber} onChange={(e) => setIdNumber(e.target.value)} placeholder="13-digit SA ID or none"
                            className="w-full mt-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                            data-testid="dep-id-number" />
                        <div className="text-[11px] text-gray-400 mt-1">Must be unique across all Medx AI accounts.</div>
                    </div>
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                        By submitting, you confirm you are the legal guardian or authorised carer and the dependent consents to data sharing where applicable.
                    </div>
                </div>
                <div className="p-5 border-t border-gray-100 flex justify-end gap-2 bg-gray-50">
                    <button type="button" onClick={onClose} className="btn-outline text-sm">Cancel</button>
                    <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="dep-submit">
                        <UserPlus className="w-4 h-4" /> {busy ? "Submitting…" : "Submit for review"}
                    </button>
                </div>
            </form>
        </div>
    );
}
